import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "@/i18n-js/instance";
import { OtpChallengeModal } from "@circle-react/components/OtpChallengeModal";

interface OtpModalState {
  error: string | null;
  isLoading: boolean;
  isOpen: boolean;
}

type RetryWithCode<T> = (code: string) => Promise<T>;

interface OtpChallengeController {
  runWithOtp: <T>(retryWithOtp: RetryWithCode<T>) => Promise<T>;
}

const OtpChallengeContext = createContext<OtpChallengeController | null>(null);
OtpChallengeContext.displayName = "OtpChallengeContext";

export const useOtpChallengeController = () => {
  const ctx = useContext(OtpChallengeContext);
  if (!ctx) throw new Error("OtpChallengeProvider is missing");
  return ctx;
};

type Pending = {
  retryWithOtp: (code: string) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
} | null;

export const OtpChallengeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<OtpModalState>({
    error: null,
    isLoading: false,
    isOpen: false,
  });

  const desiredMutationRef = useRef<Pending>(null);

  const close = useCallback(() => {
    setState({ isOpen: false, isLoading: false, error: null });
    desiredMutationRef.current = null;
  }, []);

  const runWithOtp = useCallback(
    <T,>(retryWithOtp: RetryWithCode<T>) =>
      new Promise<T>((resolve, reject) => {
        desiredMutationRef.current = {
          retryWithOtp: (code: string) =>
            retryWithOtp(code) as Promise<unknown>,
          resolve: (value: unknown) => resolve(value as T),
          reject,
        };
        setState({ isOpen: true, isLoading: false, error: null });
      }),
    [],
  );

  const value = useMemo<OtpChallengeController>(
    () => ({ runWithOtp }),
    [runWithOtp],
  );

  const handleClose = useCallback(() => {
    const desiredMutation = desiredMutationRef.current;
    if (desiredMutation)
      desiredMutation.reject(new Error(t("otp_challenge_modal.cancelled")));
    close();
  }, [close]);

  const handleSubmit = useCallback(
    async (code: string) => {
      if (!desiredMutationRef.current) return;
      const desiredMutation = desiredMutationRef.current;
      setState(s => ({ ...s, isLoading: true, error: null }));

      try {
        const result = await desiredMutation.retryWithOtp(code.trim());
        desiredMutation.resolve(result);
        close();
      } catch (err: unknown) {
        setState({ error: null, isLoading: false, isOpen: false });
        desiredMutation.reject(err);
        desiredMutationRef.current = null;
      }
    },
    [close],
  );

  return (
    <OtpChallengeContext.Provider value={value}>
      {children}

      <OtpChallengeModal
        isOpen={state.isOpen}
        isLoading={state.isLoading}
        error={state.error || undefined}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </OtpChallengeContext.Provider>
  );
};
